import {isDefined, isDefinedAndPopulated} from 'Application/utils/typeGuards';
import {breakpoints, configType} from 'UI/utils/globalStyles';

import type {Config, SpacerObject} from '@nfq/react-grid';
import type {Media} from 'types/payload-types';

/**
 * A custom React hook that generates responsive image metadata from a Payload CMS media object.
 * This hook prepares all necessary attributes for rendering an optimized `<picture>` element including `src`, `srcSet`,
 * fallback `srcSet`, `alt` text, and responsive `sizes`. It handles both populated and string reference media inputs safely
 * and falls back to empty values when no valid image is provided. If `expectedSizes` are given, the hook generates a `sizes` string
 * using the configured breakpoints to support responsive image loading.
 *
 * @param image         A `Media` object, string ID, or `null`/`undefined` representing the image to be rendered.
 * @param expectedSizes An optional object specifying how wide the image should appear at different breakpoints (e.g., `{ md: 400 }`).
 * @returns An object containing `alt`, `src`, `srcSet`, `srcSetFallback`, and `sizes` attributes for use in a `<picture>` or `<img>` tag.
 *
 * @example
 * ```tsx
 * const { src, srcSet, srcSetFallback, alt, sizes } = usePayloadImage(image, { md: 600, lg: 800 });
 * return (
 *   <picture>
 *     <source type="image/webp" srcSet={srcSet} sizes={sizes} />
 *     <source type="image/png" srcSet={srcSetFallback} sizes={sizes} />
 *     <img src={src} alt={alt} sizes={sizes} />
 *   </picture>
 * );
 * ```
 */
export const usePayloadImage = (image: Media | string | null | undefined, expectedSizes?: SpacerObject) => {
    let srcSet = '';
    let srcSetFallback = '';
    let alt = '';
    let src = null;
    let sizes = '100vw';

    if (isDefinedAndPopulated(image)) {
        if (isDefined(image.sizes)) {
            srcSet = Object.entries(image.sizes).map(([key, value]) => {
                if (!key.includes('fallback') && value.url && value.width) {
                    return `${value.url} ${value.width}w`;
                }

                return null;
            }).filter(Boolean).join(', ');
            srcSetFallback = Object.entries(image.sizes).map(([key, value]) => {
                if (key.includes('fallback') && value.url && value.width) {
                    return `${value.url} ${value.width}w`;
                }

                return null;
            }).filter(Boolean).join(', ');
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            alt = image.alt ?? '';
            src = image.url ?? null;
        }
    }

    if (expectedSizes) {
        const configBreakpoints = (configType as Config<'light'>).breakpoints;

        sizes = breakpoints.map(breakpoint => {
            if (expectedSizes[breakpoint]) {
                return `(min-width: ${configBreakpoints?.[breakpoint]}px) ${expectedSizes[breakpoint]}px`;
            }

            return null;
        }).filter(Boolean).reverse().join(', ');
    }

    return {
        alt,
        sizes,
        src,
        srcSet,
        srcSetFallback
    };
};